/**
 * <custom-fragrance-quiz> - Web Component for the 4-step fragrance finder.
 *
 * State machine: gender → family → intensity → budget → result.
 * Each step is a <template> in the host markup that we clone into the stage.
 * Final step constructs a Shopify collection filter URL and redirects.
 *
 * Filter URL conventions (matches Shopify's native filter param syntax):
 *   filter.p.tag=koen:kvinder
 *   filter.p.m.custom.fragrance_family=floral
 *   filter.p.m.custom.fragrance_family=oriental   (multi-value: repeat key)
 *   filter.p.m.custom.concentration=Eau%20de%20Parfum
 *   filter.v.price.gte=300&filter.v.price.lte=600
 *
 * Klaviyo: a quiz_completed event fires when the user reaches the result step,
 * with the constructed filter params + count of selections. This lets us
 * segment users by stated preferences in Klaviyo flows.
 */
(function () {
  const STEPS = ["gender", "family", "intensity", "budget", "result"];

  class FragranceQuiz extends HTMLElement {
    constructor() {
      super();
      this.state = {
        gender: null,
        family: [],
        intensity: null,
        budget: null,
      };
      this.currentStep = 0;
    }

    connectedCallback() {
      this.stage = this.querySelector("[data-quiz-stage]");
      this.backBtn = this.querySelector("[data-quiz-back]");
      this.progress = this.querySelector("[data-quiz-progress]");
      this.collectionUrl = this.dataset.collectionUrl || "/collections/all";

      this.backBtn.addEventListener("click", () => this.goBack());

      // Auto-advance after a brief delay so customers see the welcome state
      setTimeout(() => this.renderStep(), 250);
    }

    renderStep() {
      const stepName = STEPS[this.currentStep];
      const tpl = this.querySelector(`[data-quiz-step="${stepName}"]`);
      if (!tpl) return;

      this.stage.innerHTML = "";
      this.stage.appendChild(tpl.content.cloneNode(true));

      // Progress + back button
      const isLast = stepName === "result";
      this.progress.textContent = isLast ? "Færdig" : `${this.currentStep + 1} / ${STEPS.length - 1}`;
      this.backBtn.hidden = this.currentStep === 0 || isLast;

      // Step-specific bindings
      this[`bind_${stepName}`]?.();

      // Focus first interactive for keyboard users
      const focusable = this.stage.querySelector("button, a, [tabindex='0']");
      focusable?.focus({ preventScroll: true });
    }

    bind_gender() {
      this.stage.querySelectorAll(".quiz__choice").forEach((btn) => {
        btn.addEventListener("click", () => {
          this.state.gender = btn.dataset.value;
          this.next();
        });
      });
    }

    bind_family() {
      const choices = this.stage.querySelectorAll(".quiz__choice");
      const nextBtn = this.stage.querySelector("[data-quiz-next]");
      const refresh = () => {
        nextBtn.disabled = this.state.family.length === 0;
      };
      choices.forEach((btn) => {
        const val = btn.dataset.value;
        if (this.state.family.includes(val)) btn.classList.add("is-active");
        btn.addEventListener("click", () => {
          const i = this.state.family.indexOf(val);
          if (i >= 0) {
            this.state.family.splice(i, 1);
            btn.classList.remove("is-active");
          } else {
            this.state.family.push(val);
            btn.classList.add("is-active");
          }
          refresh();
        });
      });
      refresh();
      nextBtn.addEventListener("click", () => this.next());
    }

    bind_intensity() {
      this.stage.querySelectorAll(".quiz__choice").forEach((btn) => {
        btn.addEventListener("click", () => {
          this.state.intensity = btn.dataset.value;
          this.next();
        });
      });
      this.stage.querySelector("[data-quiz-skip]")?.addEventListener("click", () => {
        this.state.intensity = null;
        this.next();
      });
    }

    bind_budget() {
      this.stage.querySelectorAll(".quiz__choice").forEach((btn) => {
        btn.addEventListener("click", () => {
          this.state.budget = btn.dataset.value;
          this.next();
        });
      });
      this.stage.querySelector("[data-quiz-skip]")?.addEventListener("click", () => {
        this.state.budget = null;
        this.next();
      });
    }

    bind_result() {
      const summary = this.stage.querySelector("[data-quiz-summary]");
      const cta = this.stage.querySelector("[data-quiz-cta]");
      const url = this.buildResultUrl();
      cta.href = url;
      summary.innerHTML = this.renderSummary();

      // Klaviyo segmentation: "Completed Fragrance Quiz" lets us send a
      // personalized email flow with matching products + welcome discount.
      this.fireKlaviyoEvent("Completed Fragrance Quiz", { result_url: url });

      // Signup form → Klaviyo identify + Newsletter trigger
      const form = this.stage.querySelector("[data-quiz-signup-form]");
      const emailInput = this.stage.querySelector("[data-quiz-signup-email]");
      const successMsg = this.stage.querySelector("[data-quiz-signup-success]");
      if (!form || !emailInput) return;

      form.addEventListener("submit", (e) => {
        e.preventDefault();
        const email = (emailInput.value || "").trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          emailInput.focus();
          emailInput.style.borderColor = "#800020";
          return;
        }
        try {
          if (window._learnq) {
            window._learnq.push([
              "identify",
              { $email: email, $consent: ["email"] },
            ]);
            window._learnq.push([
              "track",
              "Signed Up for Newsletter",
              {
                email,
                signup_source: "fragrance_quiz",
                quiz_gender: this.state.gender,
                quiz_families: this.state.family,
                quiz_intensity: this.state.intensity,
                quiz_budget: this.state.budget,
                quiz_result_url: url,
              },
            ]);
          }
        } catch (_) {
          /* ignore */
        }
        // Also POST to Shopify's customer endpoint so the lead lands in Shopify
        // (tagged for the welcome flow). The Klaviyo identify above is the
        // primary path; this is a belt-and-suspenders for Shopify-side CRM.
        const fd = new FormData();
        fd.append("form_type", "customer");
        fd.append("utf8", "✓");
        fd.append("contact[email]", email);
        fd.append("contact[tags]", "prospect,newsletter,fragrance-quiz");
        fd.append("contact[context]", "fragrance_quiz");
        fetch("/contact", { method: "POST", body: fd }).catch(() => {});

        form.hidden = true;
        successMsg.hidden = false;
      });
    }

    next() {
      if (this.currentStep < STEPS.length - 1) {
        this.currentStep += 1;
        this.renderStep();
        this.scrollToTop();
      }
    }

    goBack() {
      if (this.currentStep > 0) {
        this.currentStep -= 1;
        this.renderStep();
        this.scrollToTop();
      }
    }

    fireKlaviyoEvent(name, extra = {}) {
      try {
        if (window._learnq) {
          window._learnq.push([
            "track",
            name,
            {
              gender: this.state.gender,
              families: this.state.family,
              intensity: this.state.intensity,
              budget: this.state.budget,
              ...extra,
            },
          ]);
        }
      } catch (_) {
        /* ignore */
      }
    }

    scrollToTop() {
      const headerOffset = 80;
      const top = this.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }

    buildResultUrl() {
      // Strategy: use gender-collection as BASE URL (those exist as smart
      // collections with the koen:* tag rule baked in). Layer the configured
      // filters (fragrance_family, price) on top. Concentration deferred until
      // Search & Discovery admin enables it as a filter on these collections.
      //
      // Why not /collections/all + filter.p.tag=koen:kvinder?
      // Because Search & Discovery on /collections/all doesn't expose Tag as
      // a filterable key - the tag filter is silently ignored in the URL.
      // Using the gender-specific collection guarantees the koen: filter
      // applies via its smart-rule.
      const genderToCollection = {
        "koen:kvinder": "/collections/til-hende",
        "koen:maend": "/collections/til-ham",
        "koen:unisex": "/collections/unisex",
      };
      const base = genderToCollection[this.state.gender] || this.collectionUrl;

      const params = new URLSearchParams();
      this.state.family.forEach((f) => {
        params.append("filter.p.m.custom.fragrance_family", f);
      });
      if (this.state.intensity) {
        params.append("filter.p.m.custom.concentration", this.state.intensity);
      }
      if (this.state.budget) {
        const [lo, hi] = this.state.budget.split("-");
        if (lo && lo !== "0") params.append("filter.v.price.gte", lo);
        if (hi && hi !== "99999") params.append("filter.v.price.lte", hi);
      }
      const q = params.toString();
      return q ? `${base}?${q}` : base;
    }

    renderSummary() {
      const familyLabels = {
        floral: "Blomstret",
        oriental: "Oriental",
        woody: "Træagtig",
        citrus: "Citrus",
        fresh: "Frisk",
        gourmand: "Sød",
        aromatic: "Aromatisk",
      };
      const genderLabels = {
        "koen:kvinder": "Til hende",
        "koen:maend": "Til ham",
        "koen:unisex": "Unisex",
      };
      const intensityLabels = {
        EDT: "Let (EDT)",
        EDP: "Medium (EDP)",
        Parfum: "Kraftig (Parfum)",
      };
      const budgetLabels = {
        "0-300": "Under 300 kr",
        "300-600": "300-600 kr",
        "600-1000": "600-1.000 kr",
        "1000-99999": "Luksus 1.000+ kr",
      };
      const rows = [];
      if (this.state.gender) {
        rows.push(["Til", genderLabels[this.state.gender]]);
      }
      if (this.state.family.length > 0) {
        rows.push(["Stemning", this.state.family.map((f) => familyLabels[f]).join(", ")]);
      }
      if (this.state.intensity) {
        rows.push(["Intensitet", intensityLabels[this.state.intensity]]);
      }
      if (this.state.budget) {
        rows.push(["Budget", budgetLabels[this.state.budget]]);
      }
      if (rows.length === 0) {
        return "<p>Ingen filtre valgt - vi viser dig alle vores bestsellere.</p>";
      }
      const dl = rows.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join("");
      return `<dl>${dl}</dl>`;
    }
  }

  if (!customElements.get("custom-fragrance-quiz")) {
    customElements.define("custom-fragrance-quiz", FragranceQuiz);
  }
})();
